/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import { ShopLight } from "@/abilities/shop/types/shop-light";
import { SendMailsInput, useSendMailsMutation } from "@/shared/services/graphql/generated/consumer-graph-types";

export const useSendMailApi = () => {
    /**
     * Sends an email using the SendMails mutation. Returns the job ID of the email sending task.
     * Use the job ID to track the status of the email sending process.
     * @param {SendMailsInput} mailData
     * @returns {Promise<string>} email sending job ID
     * @throws {ShopLight.SendMailError} when GraphQL errors occur (e.g., rate limit)
     */
    const sendMail = async (mailData: SendMailsInput): Promise<string> => {
        const { mutate } = useSendMailsMutation();

        // Promise to handle the mutation and errors, promise is rejected by Apollo when using Promises and the default error-policy, thus we need to use try catch
        // see https://www.apollographql.com/docs/react/data/error-handling#error-handling-across-api-types for more information
        try {
            const result = await mutate({ spec: mailData });

            const jobId = result?.data?.sendMails || "";

            if (!jobId) {
                const error: ShopLight.SendMailError = {
                    status: "error",
                    errorType: "no_job_id",
                    message: "No job ID received from server",
                };
                throw error;
            }

            return jobId;
        } catch (error: any) {
            if (error.graphQLErrors && error.graphQLErrors?.length > 0) {
                const graphqlErrors: ShopLight.GraphQLError[] = error.graphQLErrors.map((err: any) => ({
                    message: err.message,
                    path: err.path,
                    extensions: err.extensions,
                }));

                // Determine error type based on error code
                const firstError = error.graphQLErrors[0];
                const errorCode = firstError?.extensions?.code;
                let errorType: ShopLight.SendMailError["errorType"] = "graphql_error";

                if (errorCode === "SEND_MAIL_RATE_LIMIT_REACHED") {
                    errorType = "rate_limit";
                }

                const graphqlError: ShopLight.SendMailError = {
                    status: "error",
                    errorType,
                    message: firstError?.message || "Failed to send email",
                    graphqlErrors,
                };

                throw graphqlError;
            } else {
                // Otherwise, wrap it in a SendMailError
                const sendMailError: ShopLight.SendMailError = {
                    status: "error",
                    errorType: "unknown",
                    message: error instanceof Error ? error.message : "Unknown error occurred while sending email",
                };
                throw sendMailError;
            }
        }
    };

    return {
        sendMail,
    };
};
